/**
 * scheduleIdleOrInteraction(callback)
 *
 * Defers a non-critical task (typically loading a 3rd-party widget script)
 * until ONE of the following triggers, whichever comes first:
 *
 *   1. `requestIdleCallback` fires after 3500 ms of main-thread idle time, OR
 *   2. The first user interaction (scroll / pointer / touch / key), OR
 *   3. 4000 ms have elapsed since this function was called (hard fallback so
 *      widgets always eventually appear on long-running idle pages).
 *
 * Returns a cleanup function that cancels pending triggers; safe to call from
 * a React `useEffect` cleanup. Idempotent — the callback is invoked at most
 * once even if multiple triggers race.
 *
 * Why this pattern?
 * -----------------
 * The Trustpilot bootstrap (~110 KiB JS + 3 long tasks) and the Tree-Nation
 * widget (~400 KiB nested Nuxt SPA that pulls in its own Google Font and CSS
 * chain — the famous "Outfit font in critical path" PSI warning) are below-
 * the-fold on every public page. Loading them eagerly during initial
 * navigation costs 1.5-2 s of LCP for zero visible benefit. Deferring until
 * idle or interaction keeps them functional while keeping LCP clean.
 */
export default function scheduleIdleOrInteraction(callback, { idleTimeout = 3500, hardTimeout = 4000 } = {}) {
    if (typeof window === "undefined") return () => {};
    let done = false;
    const fire = () => {
        if (done) return;
        done = true;
        cleanup();
        callback();
    };
    const onInteraction = () => fire();
    const events = ["scroll", "pointerdown", "touchstart", "keydown"];
    events.forEach((e) => window.addEventListener(e, onInteraction, { once: true, passive: true }));
    let idleHandle = null;
    let hardHandle = null;
    const w = window;
    if (typeof w.requestIdleCallback === "function") {
        idleHandle = w.requestIdleCallback(fire, { timeout: idleTimeout });
    } else {
        idleHandle = setTimeout(fire, idleTimeout);
    }
    hardHandle = setTimeout(fire, hardTimeout);
    const cleanup = () => {
        events.forEach((e) => window.removeEventListener(e, onInteraction));
        if (idleHandle != null) {
            if (typeof w.cancelIdleCallback === "function" && typeof idleHandle === "number") {
                w.cancelIdleCallback(idleHandle);
            } else {
                clearTimeout(idleHandle);
            }
        }
        if (hardHandle != null) clearTimeout(hardHandle);
    };
    return cleanup;
}
